<template>
	<nta-page-layout>
		<template #default>
			<div style="width: 100%; height: 100%" class="d-flex justify-center align-center">
				<!-- Banner -->
				<AlternisBanner
					style="position: absolute; top: 50px; left: 0px"
					:headline="partner?.name"
					:logo="partner?.logo"
				/>

				<!-- LEFT -->
				<div style="width: 50%; height: 100%; overflow: hidden">
					<AlternisGlobe
						:showLines="true"
						:showRegions="false"
						:showLabels="false"
						style="margin-top: 250px; margin-left: 100px"
					/>
				</div>

				<!-- RIGHT -->
				<div style="width: 50%; height: 100%">
					<div style="width: 100%; height: 100%" class="d-flex justify-center align-center">
						<v-card v-if="!partner" color="contained" rounded="lg" width="500px">
							<v-card-title style="text-align: center">
								<h2>KT Voicebots</h2>
							</v-card-title>
							<v-card-text class="d-flex justify-center align-center flex-wrap">
								<v-btn
									v-for="p in partners"
									:key="p.id"
									style="width: 500px; height: 70px; font-size: large"
									class="ma-2"
									@click="router.push({ name: 'Krankentransport ' + p.name })"
									><img :src="p.logo" style="height: 40px; width: auto; margin-right: 10px" />{{
										p.name
									}}</v-btn
								>
							</v-card-text>
						</v-card>

						<!-- WORKSPACES -->
						<v-card v-else color="contained" rounded="lg">
							<v-card-text>
								<v-list
									v-if="workspaces.length > 0"
									style="max-height: 40vh; overflow-y: auto; padding: 0"
									rounded="lg"
								>
									<v-list-item
										v-for="workspace in workspaces"
										:key="workspace.id"
										class="d-flex align-center"
										title="KT Voicebot Anruf"
										:subtitle="new Date(workspace.timestamp).toLocaleString('de-DE')"
										prepend-icon="mdi-phone-in-talk"
										@click="workspaceClick(workspace.id)"
									>
										<template #append>
											<v-icon v-show="workspace.new" size="x-large" color="#2F5384">
												mdi-new-box
											</v-icon>
											<v-icon v-show="!workspace.new" size="x-large" color="success">
												mdi-check-mark
											</v-icon>
										</template>
									</v-list-item>
								</v-list>
								<v-alert v-else>
									<div>Noch keine neuen Calls eingetroffen.</div>
								</v-alert>
							</v-card-text>
						</v-card>
					</div>
				</div>

				<!-- Footer -->
				<div style="position: absolute; bottom: 0; left: 0">
					<PartnerLogo />
				</div>
				<div style="position: absolute; bottom: 0; right: 0">
					<PhoneCallQRCode
						v-if="partner"
						:qrCode="partner.qrCode"
						:telephoneNumber="partner.telephoneNumber"
					/>
				</div>
			</div>
		</template>
	</nta-page-layout>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { api } from "@lr-notitia/notitia-operator/client";
import AlternisGlobe from "../components/AlternisGlobe.vue";
import AlternisBanner from "../components/AlternisBanner.vue";
import PartnerLogo from "../components/PartnerLogo.vue";
import PhoneCallQRCode from "../components/PhoneCallQRCode.vue";
import { theme } from "@lr-notitia/notitia-ui";
import { useRoute, useRouter } from "vue-router";

theme.vuetifyTheme.value = "dark";
const route = useRoute();
const router = useRouter();

interface Partner {
	id: string;
	name: string;
	domainId: string;
	instanceDataspaceId: string;
	logo: string;
	qrCode: string;
	telephoneNumber: string;
}

const partners: Partner[] = [
	{
		id: "krankentransport-fr",
		name: "Freiburg/Hochschwarzwald",
		domainId: "99e38c6b-23e7-47ef-8eb3-451372171c90",
		instanceDataspaceId: "ac9bf8fd-a99c-4084-bb3c-d35de2611f46",
		logo: "https://assets.livereader.com/asset/Or1G5CfZk3ryU2QR.png", // LK Freiburg Hochschwarzwald CoA
		qrCode: "https://assets.livereader.com/asset/dokYl55c6R79SZHp.png",
		telephoneNumber: "02408/9382-520",
	},
	{
		id: "krankentransport-sb",
		name: "Saarland",
		domainId: "99e38c6b-23e7-47ef-8eb3-451372171c90",
		instanceDataspaceId: "50b7678c-6073-405e-a57c-7537f0204ab6",
		logo: "https://assets.livereader.com/asset/uEtrptFFXnNeKRRo.png", // Saarland CoA
		qrCode: "https://assets.livereader.com/asset/siuwFqzk5kYCi7uS.png",
		telephoneNumber: "02408/9382-536",
	},
];

const partnerId = ref<string>(route.path.split("/")[route.path.split("/").length - 1]);
const partner = ref<Partner | null>(partners.find((p) => p.id === partnerId.value) || null);

const workspaces = ref<
	{
		id: string;
		timestamp: number;
		new: boolean;
	}[]
>([]);

function workspaceClick(id: string) {
	if (!workspaces.value || !partner.value) return;

	const workspace = workspaces.value.find((w) => w.id === id);
	if (!workspace) return;
	workspace.new = false;

	const protocol = window.location.protocol; // http: or https:
	const hostname = window.location.hostname; // operator.notitia.dev or operator.notitia.world
	const url = `${protocol}//${hostname}/${partner.value.domainId}/template/${partner.value.instanceDataspaceId}/template/${id}`;
	window.open(url, "_blank");
}

watch(
	() => partner.value,
	(newVal, oldVal) => {
		if (oldVal) {
			api().socket().domain(oldVal.domainId).instance().dataspace(oldVal.instanceDataspaceId).off();
		}
		if (newVal) {
			api()
				.socket()
				.domain(newVal.domainId)
				.instance()
				.dataspace(newVal.instanceDataspaceId)
				.on((message) => {
					const newId = message.workspaces[message.workspaces.length - 1];
					const newWorkspace = {
						id: newId,
						timestamp: Date.now(),
						new: true,
					};
					workspaces.value = [newWorkspace, ...workspaces.value];
					workspaceClick(newId);
				});
		}
	},
	{ immediate: true },
);

watch(
	() => route.path,
	(newVal, oldVal) => {
		partnerId.value = newVal.split("/")[newVal.split("/").length - 1];
		partner.value = partners.find((p) => p.id === partnerId.value) || null;
	},
);
</script>

<style scoped lang="scss">
.active-call-item {
	cursor: pointer;
	transition: background-color 0.3s ease;

	&:hover {
		background-color: rgba(47, 83, 132, 0.1);
	}
}

.ringing-phone {
	animation: ring 1.5s ease-in-out infinite;
	transform-origin: center center;
}

@keyframes ring {
	0% {
		transform: rotate(0deg);
	}
	5% {
		transform: rotate(15deg);
	}
	10% {
		transform: rotate(-13deg);
	}
	15% {
		transform: rotate(14deg);
	}
	20% {
		transform: rotate(-12deg);
	}
	25% {
		transform: rotate(10deg);
	}
	30% {
		transform: rotate(-8deg);
	}
	35% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(0deg);
	}
}
</style>
